import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { Card, Badge, Spinner, Button } from '../../components/ui';
import { 
  BarChart2, TrendingUp, TrendingDown, Clock, CheckCircle, 
  XCircle, Send, Download, Calendar, Filter, RefreshCw,
  Users, Mail, Zap, AlertCircle, ArrowUpRight, ArrowDownRight,
  ChevronRight, Search, FileText, Eye, Activity, Target
} from 'lucide-react';
import './Reports.css';

const Reports = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { request } = useApi();
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await request('get', '/api/send/jobs').catch(() => []);
      setJobs(data || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesFilter = filter === 'all' || job.status === filter.toUpperCase();
    const matchesSearch = job.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const totals = {
    sent: jobs.reduce((sum, j) => sum + (j.sentCount || 0), 0),
    delivered: jobs.reduce((sum, j) => sum + (j.deliveredCount || 0), 0),
    read: jobs.reduce((sum, j) => sum + (j.readCount || 0), 0),
    failed: jobs.reduce((sum, j) => sum + (j.failedCount || 0), 0),
    campaigns: jobs.length,
    recipients: jobs.reduce((sum, j) => sum + (j.totalRecipients || 0), 0)
  };

  const deliveryRate = totals.sent > 0 ? ((totals.delivered / totals.sent) * 100).toFixed(1) : 0;
  const successRate = totals.sent > 0 ? (((totals.sent - totals.failed) / totals.sent) * 100).toFixed(1) : 0;
  const readRate = totals.delivered > 0 ? ((totals.read / totals.delivered) * 100).toFixed(1) : 0;

  const statCards = [
    { 
      icon: Send, 
      label: 'Total Sent', 
      value: totals.sent.toLocaleString(), 
      color: '#0066FF',
      bg: 'rgba(0,102,255,0.08)',
      trend: '+12%',
      trendUp: true
    },
    { 
      icon: CheckCircle, 
      label: 'Delivered', 
      value: totals.delivered.toLocaleString(), 
      color: '#00A86B',
      bg: 'rgba(0,168,107,0.08)',
      trend: '+8%',
      trendUp: true
    },
    { 
      icon: Eye, 
      label: 'Read', 
      value: totals.read.toLocaleString(), 
      color: '#FF6B35',
      bg: 'rgba(255,107,53,0.08)',
      trend: '+5%',
      trendUp: true
    },
    { 
      icon: TrendingUp, 
      label: 'Delivery Rate', 
      value: `${deliveryRate}%`, 
      color: '#00B4D8',
      bg: 'rgba(0,180,216,0.08)',
      trend: '+5%',
      trendUp: true
    }
  ];

  const getStatusBadge = (status) => {
    const configs = {
      'COMPLETED': { variant: 'success', label: 'Completed', icon: <CheckCircle size={12} /> },
      'PROCESSING': { variant: 'warning', label: 'Processing', icon: <RefreshCw size={12} /> },
      'PENDING': { variant: 'default', label: 'Pending', icon: <Clock size={12} /> },
      'FAILED': { variant: 'danger', label: 'Failed', icon: <XCircle size={12} /> },
      'PARTIAL': { variant: 'warning', label: 'Partial', icon: <AlertCircle size={12} /> },
    };
    const config = configs[status] || configs['PENDING'];
    return <Badge variant={config.variant}>{config.icon} {config.label}</Badge>;
  };

  // FIX: Don't show progress for failed jobs
  const getProgressPercent = (job) => {
    if (!job.totalRecipients) return 0;
    if (job.status === 'FAILED') return 0;
    const sent = (job.sentCount || 0) + (job.failedCount || 0);
    return Math.round((sent / job.totalRecipients) * 100);
  };

  const getProgressColor = (job) => {
    if (job.status === 'FAILED') return '#EF4444';
    if (job.status === 'COMPLETED') return '#00A86B';
    return '#0066FF';
  };

  const filters = [
    { value: 'all', label: 'All Campaigns', icon: BarChart2 },
    { value: 'completed', label: 'Completed', icon: CheckCircle },
    { value: 'processing', label: 'Processing', icon: RefreshCw },
    { value: 'pending', label: 'Pending', icon: Clock },
    { value: 'failed', label: 'Failed', icon: XCircle },
  ];

  if (loading) {
    return (
      <div className="reports-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h1 className="reports-title">Campaign Analytics</h1>
          <p className="reports-subtitle">Monitor your message delivery performance and campaign metrics</p>
        </div>
        <div className="header-actions">
          <button className="refresh-btn" onClick={fetchJobs}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button className="export-btn">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview">
        {statCards.map((card, idx) => (
          <div className="stat-card-large" key={idx}>
            <div className="stat-card-header">
              <div className="stat-icon-large" style={{ background: card.bg, color: card.color }}>
                <card.icon size={22} />
              </div>
              <div className={`stat-trend ${card.trendUp ? 'up' : 'down'}`}>
                {card.trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                {card.trend}
              </div>
            </div>
            <div className="stat-value-large">{card.value}</div>
            <div className="stat-label-large">{card.label}</div>
          </div>
        ))}
      </div>

      {/* Quick Metrics Bar */}
      <div className="metrics-bar">
        <div className="metric-item">
          <div className="metric-icon blue">
            <Users size={18} />
          </div>
          <div>
            <div className="metric-value">{totals.campaigns}</div>
            <div className="metric-label">Campaigns</div>
          </div>
        </div>
        <div className="metric-divider" />
        <div className="metric-item">
          <div className="metric-icon teal">
            <Mail size={18} />
          </div>
          <div>
            <div className="metric-value">{totals.recipients.toLocaleString()}</div>
            <div className="metric-label">Recipients</div>
          </div>
        </div>
        <div className="metric-divider" />
        <div className="metric-item">
          <div className="metric-icon green">
            <Activity size={18} />
          </div>
          <div>
            <div className="metric-value">{successRate}%</div>
            <div className="metric-label">Success Rate</div>
          </div>
        </div>
        <div className="metric-divider" />
        <div className="metric-item">
          <div className="metric-icon orange">
            <Target size={18} />
          </div>
          <div>
            <div className="metric-value">{readRate}%</div>
            <div className="metric-label">Read Rate</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="reports-toolbar">
        <div className="filters-bar">
          <div className="filters-label">
            <Filter size={14} />
            <span>Filter:</span>
          </div>
          <div className="filters-group">
            {filters.map((f) => (
              <button
                key={f.value}
                className={`filter-chip ${filter === f.value ? 'active' : ''}`}
                onClick={() => setFilter(f.value)}
              >
                <f.icon size={14} />
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="search-bar">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Campaigns Table */}
      <Card className="campaigns-table-container" padding="none">
        <div className="table-header">
          <div className="table-header-left">
            <h3 className="table-title">Campaign History</h3>
            <span className="campaign-count">{filteredJobs.length} campaigns</span>
          </div>
          <button className="view-all-btn">
            View All <ChevronRight size={16} />
          </button>
        </div>
        <div className="table-responsive">
          <table className="campaigns-table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Date</th>
                <th>Recipients</th>
                <th>Sent</th>
                <th>Delivered</th>
                <th>Read</th>
                <th>Failed</th>
                <th>Progress</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={9}>
                    <div className="empty-state">
                      <BarChart2 size={48} className="empty-icon" />
                      <h3>No campaigns yet</h3>
                      <p>Create your first campaign to start tracking performance</p>
                      <button className="empty-link" onClick={() => navigate('/send')}>
                        Create Campaign <ChevronRight size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => (
                  <tr key={job.id} className={job.status === 'FAILED' ? 'row-failed' : ''}>
                    <td className="campaign-name">
                      <div className="campaign-name-wrapper">
                        <span>{job.name}</span>
                        {job.status === 'FAILED' && (
                          <span className="failed-badge">Failed</span>
                        )}
                      </div>
                    </td>
                    <td className="campaign-date">
                      <Calendar size={12} />
                      {new Date(job.createdAt).toLocaleDateString()}
                    </td>
                    <td>{job.totalRecipients}</td>
                    <td className="sent-count">{job.sentCount || 0}</td>
                    <td className="delivered-count">{job.deliveredCount || 0}</td>
                    <td className="read-count">{job.readCount || 0}</td>
                    <td className={job.failedCount > 0 ? 'failed-count' : ''}>
                      {job.failedCount || 0}
                    </td>
                    <td className="progress-cell">
                      <div className="progress-wrapper">
                        <div className="progress-bar-bg">
                          <div 
                            className={`progress-bar-fill ${job.status === 'FAILED' ? 'failed' : ''}`}
                            style={{ 
                              width: `${getProgressPercent(job)}%`,
                              background: getProgressColor(job)
                            }}
                          />
                        </div>
                        <span className="progress-text">
                          {job.status === 'FAILED' ? '0%' : `${getProgressPercent(job)}%`}
                        </span>
                      </div>
                    </td>
                    <td>{getStatusBadge(job.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Insights Section */}
      {jobs.length > 0 && (
        <div className="insights-section">
          <div className="insights-header">
            <Zap size={20} />
            <h3>Performance Insights</h3>
          </div>
          <div className="insights-grid">
            <div className="insight-card">
              <div className="insight-value">{deliveryRate}%</div>
              <div className="insight-label">Delivery Rate</div>
              <div className="insight-trend up">Above average</div>
            </div>
            <div className="insight-card">
              <div className="insight-value">{readRate}%</div>
              <div className="insight-label">Read Rate</div>
              <div className="insight-trend up">Strong engagement</div>
            </div>
            <div className="insight-card">
              <div className="insight-value">{Math.round(totals.sent / (jobs.length || 1)).toLocaleString()}</div>
              <div className="insight-label">Avg per Campaign</div>
              <div className="insight-trend neutral">Typical volume</div>
            </div>
            <div className="insight-card">
              <div className="insight-value">{totals.failed > 0 ? Math.round((totals.failed / totals.sent) * 100) : 0}%</div>
              <div className="insight-label">Failure Rate</div>
              {totals.failed > 0 ? (
                <div className="insight-trend down">Check invalid numbers</div>
              ) : (
                <div className="insight-trend up">Excellent delivery</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;